<template>
  <v-dialog v-model="visible"
    persistent
    width="1200"
    scrollable>
    <v-card height="550">
      <v-card-title>
        {{ selectedCase.name }}
        &nbsp;
        <span v-if="selectedCase.title"
          class="font-weight-light">{{ selectedCase.title }}</span>
      </v-card-title>
      <v-card-subtitle>
        <span v-if="selectedCase.id">ID: <code>{{ selectedCase.id }}</code></span>
        <span v-if="selectedCase.localId">&nbsp;&nbsp;&nbsp;&nbsp; Local ID: <code>{{ selectedCase.localId }}</code></span>
      </v-card-subtitle>
      <v-card-text>

        <!-- Tabs -->
        <v-tabs v-model="tab"
          align-tabs="title">

          <v-tab key="general">
            General
          </v-tab>
          <v-tab key="tax">
            Tax & Other IDs
          </v-tab>
          <v-tab key="history">
            Case History
          </v-tab>

          <v-tabs-items v-model="tab">

            <!-- General tab -->
            <v-tab-item key="general">
              <v-card flat
                min-height="300">
                <v-card-text>
                  <v-row>

                    <!-- Left col -->
                    <v-col cols="8">
                      <v-row dense>
                        <!-- Address  -->
                        <v-col cols="6">
                          <span class="overline font-weight-black">Address</span>
                          <div v-if="addressBlank(selectedCase.address)"
                            cols="6">
                            N/A
                          </div>
                          <div v-else
                            cols="6">
                            {{ selectedCase.address.address1 }}
                            {{ selectedCase.address.address2 }}
                            {{ selectedCase.address.address3 }}
                            {{ selectedCase.address.address4 }}
                            <br />
                            {{ selectedCase.address.city }}
                            {{ selectedCase.address.stateOrProvince }}
                            {{ selectedCase.address.postalCode }}
                            <br />
                            {{ selectedCase.address.country }}
                          </div>
                        </v-col>

                        <!-- Phone  -->
                        <v-col cols="3">
                          <div class="overline font-weight-black">Phone Number</div>
                          <span v-if="selectedCase.phoneNumber">
                            {{ selectedCase.phoneNumber }}
                          </span>
                          <span v-else>N/A</span>
                        </v-col>

                      </v-row>

                      <!-- Individual properties -->
                      <v-row v-if="!selectedCase.type || selectedCase.type == 'individual'"
                        dense>
                        <!-- DOB  -->
                        <v-col cols="3">
                          <div class="overline font-weight-black">DOB</div>
                          {{ selectedCase.dob ? selectedCase.dob : 'N/A' }}
                        </v-col>
                        <!-- Citizenship -->
                        <v-col cols="3">
                          <div class="overline font-weight-black">Citizenship</div>
                          <span>
                            {{ selectedCase.citizenship ? selectedCase.citizenship : 'N/A' }}
                          </span>
                        </v-col>
                        <!-- Birthplace -->
                        <v-col cols="3">
                          <div class="overline font-weight-black">Birthplace</div>
                          <span>
                            {{ selectedCase.placeOfBirth ? selectedCase.placeOfBirth : 'N/A' }}
                          </span>
                        </v-col>
                        <!-- Nationality -->
                        <v-col cols="3">
                          <div class="overline font-weight-black">Nationality</div>
                          <span>
                            {{ selectedCase.nationality ? selectedCase.nationality : 'N/A' }}
                          </span>
                        </v-col>
                      </v-row>


                      <v-row dense>
                        <!-- Passport -->
                        <v-col cols="6">
                          <div class="overline font-weight-black">Passport</div>
                          <span v-if="selectedCase.passport && selectedCase.passport.passportNumber">
                            {{ selectedCase.passport.passportNumber }}
                            {{ selectedCase.passport.passportCountry ? selectedCase.passport.passportCountry : 'Country Unknown' }}
                          </span>
                          <span v-else>
                            N/A
                          </span>
                        </v-col>

                        <v-col cols="6">
                          <div class="overline font-weight-black">Digital Currency Address</div>
                          <span>
                            {{ selectedCase.digitalCurrencyAddress ? selectedCase.digitalCurrencyAddress : 'N/A' }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Right col -->
                    <!-- Case fields  -->
                    <v-col cols="4">
                      <span v-if="selectedCase.customFields && Object.keys(selectedCase.customFields).length > 0">
                        <v-row align="center">
                          <v-col v-for="(value, propertyName) in selectedCase.customFields"
                            :key="propertyName"
                            cols="6">
                            <div class="overline font-weight-black">{{ propertyName }}</div>
                            {{ value }}
                          </v-col>
                        </v-row>
                      </span>
                    </v-col>

                    <!-- Case labels -->
                    <v-col cols="4">
                      <span v-if="selectedCase.customLabels && Object.keys(selectedCase.customLabels).length > 0">
                        <v-row align="center">
                          <v-col v-for="(value, propertyName) in selectedCase.customLabels"
                            :key="propertyName"
                            cols="6">
                            <div class="overline font-weight-black">{{ propertyName }}</div>
                            {{ value }}
                          </v-col>
                        </v-row>
                      </span>
                    </v-col>

                    <v-col cols="4">
                      <span v-if="selectedCase.caseAttributes && Object.keys(selectedCase.caseAttributes).length > 0">
                        <v-row align="center">
                          <v-col cols="6">
                            <h3>Other Attributes</h3>
                          </v-col>
                          <v-col>
                            <v-divider />
                          </v-col>
                        </v-row>
                        <v-row dense>
                          <v-col v-for="(value, propertyName) in selectedCase.caseAttributes"
                            :key="propertyName"
                            cols="6">
                            <div class="overline">{{ propertyName }}</div>
                            {{ value }}
                          </v-col>
                        </v-row>
                      </span>
                      <!-- Attributes -->
                    </v-col>

                  </v-row>

                  <!-- Last updated -->
                  <v-row class="mt-10">
                    <v-col>
                      <p class="font-italic"
                        v-if="selectedCase.createdBy">Created {{ formatDateTime(selectedCase.createdOn) }} by {{ selectedCase.createdBy }}</p>
                      <p class="font-italic"
                        v-if="selectedCase.modifiedOn">Last modified {{ formatDateTime(selectedCase.modifiedOn) }} by {{ selectedCase.modifiedBy }}</p>
                      <p class="font-italic"
                        v-if="selectedCase.whitelisted">Whitelisted {{ formatDateTime(selectedCase.whitelistedOn) }} by {{ selectedCase.whitelistedBy }}</p>
                    </v-col>
                  </v-row>

                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Tax tab -->
            <v-tab-item key="tax">
              <v-card flat
                min-height="300">
                <v-card-text>
                  <!-- Crypto -->
                  <v-row v-if="selectedCase.cryptoWallet">
                    <v-col cols="4">
                      <v-card>
                        <v-card-title>
                          Cryptocurrency Wallet</v-card-title>
                        <v-card-subtitle>{{ selectedCase.cryptoWallet.walletName }}</v-card-subtitle>
                        <v-card-text><code>{{ selectedCase.cryptoWallet.walletId }}</code></v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row v-if="selectedCase.identification === undefined || selectedCase.identification.length == 0">
                    <v-col cols="4">
                      <v-card>
                        <!-- <v-card-title>Cryptocurrency Wallet</v-card-title>
                        <v-card-subtitle>{{ selectedCase.cryptoWallet.walletName }}</v-card-subtitle> -->
                        <v-card-text>No Tax ID information available.</v-card-text>
                      </v-card>

                    </v-col>
                  </v-row>
                  <v-row v-else>
                    <v-col cols="6"
                      v-for="(caseId, index) in selectedCase.identification"
                      :key="index">

                      <!-- Type -->
                      <v-row align="center">
                        <v-col cols="4">
                          <h3>{{ caseId.type }}</h3>
                        </v-col>
                        <v-col>
                          <v-divider />
                        </v-col>
                      </v-row>

                      <!-- ID Number / Country -->
                      <v-row dense
                        class="mb-4">
                        <v-col>
                          <div class="overline font-weight-black">ID</div>
                          {{ caseId.idNumber ? caseId.idNumber : "N/A" }}
                        </v-col>
                        <v-col>
                          <div class="overline font-weight-black">Country</div>
                          {{ caseId.country ? caseId.country : "N/A" }}
                        </v-col>
                      </v-row>

                      <!-- Status -->
                      <span v-if="caseId.status && caseId.status != 'UNVERIFIED'">
                        <v-row>
                          <v-col>
                            <div class="overline font-weight-black">Status</div>

                            <!-- Valid -->
                            <span v-if="caseId.status == 'VALID'">
                              <v-alert type="success"
                                dense>
                                <span class="font-weight-black font-italic mr-1">Valid</span> as of {{ formatDateTime(caseId.validationDate) }}
                              </v-alert>
                              <!-- <v-alert type="info" dense>Modifying ID information will cancel valid status and reschedule validation.</v-alert> -->
                            </span>

                            <!-- Invalid -->
                            <span v-else-if="caseId.status == 'INVALID'">
                              <v-alert type="error"
                                dense>
                                <span class="font-weight-black font-italic">Invalid</span>
                              </v-alert>
                              <!-- <v-alert type="info" dense>Update ID information to reattempt validation.</v-alert> -->
                            </span>

                            <span v-else>
                              <v-alert type="info"
                                dense>
                                <span class="font-weight-black font-italic">Unknown</span>
                                <p>Status is unknown at this time.</p>
                              </v-alert>
                            </span>
                          </v-col>
                        </v-row>
                        <v-row v-if="caseId.validationDetails">

                          <v-col v-for="(value, key, index) in caseId.validationDetails.details"
                            :key="index">
                            <div class="overline font-weight-black">{{ key }}</div>
                            <span>
                              {{ value }}
                            </span>
                          </v-col>
                        </v-row>
                      </span>
                      <span v-else>
                        <!-- Unverified or empty -->
                        <v-alert type="warning"
                          dense>
                          <span class="font-weight-black font-italic">Unverified</span>
                          <p>Validation is scheduled, check back at a later time.</p>
                        </v-alert>
                      </span>

                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- History tab -->
            <v-tab-item key="history">
              <v-card flat
                min-height="300">
                <v-card-title>
                  Case Screening and Compliance History
                </v-card-title>
                <v-card-text>
                  <v-data-table dense
                    class="pt-2"
                    :headers="historyHeaders"
                    :items="selectedCaseHistory ? selectedCaseHistory.screeningHistory : {}"
                    no-data-text="No screening history.">

                    <template v-slot:item.screenDate="{ item }">
                      {{ formatDateTime(item.screenDate) }}
                    </template>

                    <template v-slot:item.caseReportId="{ item }">
                      <span v-if="item.caseReportId">
                        <v-btn icon
                          x-small
                          color="blue"
                          @click="openCaseReport(item.caseReportId)">
                          <v-icon dark>mdi-export</v-icon>
                        </v-btn>
                      </span>
                    </template>

                    <template v-slot:item.matchCount="{ item }">
                      <span v-if="item.matchCount > 0">
                        {{ item.matchCount }}
                      </span>
                    </template>

                    <template v-slot:item.type="{ item }">
                      <span v-if="item.type == 'SANCTIONS_SCREENING'">
                        Sanctions screening
                      </span>
                      <span v-else-if="item.type == 'CASE_GROUP_SANCTIONS_SCREENING'">
                        Sanctions screening by Case Group
                      </span>
                      <span v-else-if="item.type == 'CASE_GROUP_VAT_VALIDATION'">
                        VAT validation by Case Group
                      </span>
                      <span v-else-if="item.type == 'CASE_GROUP_TIN_VALIDATION'">
                        TIN Validation by Case Group
                      </span>
                    </template>

                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>


          </v-tabs-items>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1"
          text
          @click="onCancel">{{
            $t("label.close")
          }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { formatDateTime } from "../../utils/dateUtils";
  import axios from "axios";
  export default {
    name: "ViewCase",
    props: {
      selectedCase: {},
      selectedCaseHistory: {},
      visible: {
        type: Boolean,
        default: false,
      },
      showVatStatus: {
        type: Boolean,
        default: false,
      },
      showTinStatus: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        tab: null,
        historyHeaders: [
          { text: "Date", value: "screenDate" },
          { text: "Type", value: "type" },
          { text: "By", value: "screenedByUserName" },
          // { text: "Sources", value: "sources" },
          { text: "Matches", value: "matchCount", align: "center" },
          { text: "Case Report", value: "caseReportId", sortable: false, align: "right" }
        ]
      }
    },
    methods: {
      formatDateTime,
      openCaseReport(caseReportId) {
        // Get case report with ID and push to report view
        axios.get('/api/caseReport/' + caseReportId)
          .then((res) => {
            this.$router.push({
              name: "casereport",
              params: {
                caseReport: res.data,
              },
            });
          })
          .catch((error) => {
            console.log(error)
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertTitle: "Oops.",
              alertMessage: "There was an problem locating this Case Report.",
            });
          })
      },
      addressBlank(address) {
        if (address) {
          return ![address.address1,
          address.address2,
          address.address3,
          address.address4,
          address.city,
          address.stateOrProvince,
          address.postalCode,
          address.country].some(Boolean)
        } else {
          return true
        }

      },
      onCancel() {
        this.$emit("onCancelled");
      }
    }
  }
</script>